import { useMemo, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  acceptTask,
  ApiError,
  arriveTask,
  cancelTask,
  deleteTask,
  fetchTask,
  fetchTaskEvents,
  startTask,
} from '../api/client';
import type { FieldTask, FieldTaskEvent } from '../api/types';
import { useSession } from '../app/WorkforceContext';
import { useAsync } from '../app/useAsync';
import { useGeolocation } from '../geo/useGeolocation';
import { formatAccuracy, formatDateInZone, formatDistance, formatTimeInZone } from '../lib/format';
import { createIdempotencyKeyHolder } from '../lib/idempotency';
import {
  availableActions,
  canDeleteTask,
  formatAction,
  formatTaskStatus,
  pernahMenjadiPelaksana,
  requiresLocation,
  wasReassignedAway,
  type FieldTaskStatus,
  type TaskAction,
} from '../lib/task-actions';
import { TrailMap } from '../components/TrailMap';
import {
  formatEventType,
  formatPriority,
  lompatanMencurigakan,
  navigationLinks,
  sortEvents,
} from '../lib/task-events';
import { buildTaskTrail, destinationOf } from '../lib/task-trail';
import { tautanKembaliTugas } from '../lib/task-back-link';
import { buktiSampai, lamaSingkat, tahapanTugas, totalMenit } from '../lib/task-durations';
import { EmptyState, ErrorCard, LoadingState } from '../states';
import { DeliverySection } from './DeliverySection';
import { TaskDocumentsSection } from './TaskDocumentsSection';

/**
 * Detail tugas + tombol aksi.
 *
 * Tombol yang muncul ditentukan `availableActions` dari STATUS SERVER — bukan dari tindakan
 * terakhir pengguna dan bukan dari tebakan optimistis. Setelah setiap transisi, halaman memuat
 * ulang dari server alih-alih menebak status berikutnya: bila orang lain mengubah tugas ini
 * bersamaan, yang benar adalah jawaban server, bukan perkiraan kita.
 */
const EVENTS_KOSONG: readonly FieldTaskEvent[] = [];

export function TaskDetailPage(): ReactNode {
  const session = useSession();
  const { id = '' } = useParams<{ id: string }>();
  // Di ATAS seluruh return bersyarat — aturan hook, yang baru saja menjatuhkan halaman ini.
  const navigate = useNavigate();
  const locationState = useLocation().state as { readonly asal?: unknown } | null;
  const kembali = tautanKembaliTugas(locationState?.asal);
  const timezone = session.policy?.timezone ?? 'Asia/Jakarta';
  const geo = useGeolocation();

  const [busy, setBusy] = useState<TaskAction | null>(null);
  const [actionError, setActionError] = useState<ApiError | null>(null);
  /*
   * Konfirmasi DUA LANGKAH, bukan `window.confirm`.
   *
   * Dialog bawaan memblokir seluruh halaman dan di PWA sebagian peramban menyembunyikannya sama
   * sekali — tombol yang menghapus tanpa dialog yang terlihat adalah cara terburuk fitur ini bisa
   * gagal. Dua langkah di dalam halaman selalu terlihat, dan langkah keduanya menyebut apa yang
   * akan hilang.
   */
  const [konfirmasiHapus, setKonfirmasiHapus] = useState(false);
  // Satu pemegang kunci per halaman: percobaan ulang niat yang sama memakai kunci sama,
  // dan kunci diputar hanya setelah niat itu selesai.
  const keys = useMemo(() => createIdempotencyKeyHolder(), []);

  const { state, reload } = useAsync(async () => {
    const [task, events] = await Promise.all([
      fetchTask(session.auth, id),
      fetchTaskEvents(session.auth, id),
    ]);
    return { task, events };
  }, [session.auth, id]);

  async function run(action: TaskAction, task: FieldTask): Promise<void> {
    setBusy(action);
    setActionError(null);
    const key = keys.current();
    const location = {
      latitude: geo.location.latitude,
      longitude: geo.location.longitude,
      accuracyMeters: geo.location.accuracyMeters,
      note: null,
    };
    try {
      if (action === 'accept') await acceptTask(session.auth, task.id, key);
      else if (action === 'start') await startTask(session.auth, task.id, key, location);
      else if (action === 'arrive') await arriveTask(session.auth, task.id, key, location);
      else if (action === 'cancel') await cancelTask(session.auth, task.id, key);
      keys.rotate();
      reload();
    } catch (caught: unknown) {
      // Kunci TIDAK diputar saat gagal — percobaan ulang harus memakai kunci yang sama supaya
      // kegagalan jaringan setelah server menerima tidak menghasilkan event kedua.
      setActionError(
        caught instanceof ApiError
          ? caught
          : new ApiError(0, 'UNKNOWN', 'Kesalahan tidak dikenal.', null),
      );
    } finally {
      setBusy(null);
    }
  }

  /*
   * HOOK WAJIB DIPANGGIL SEBELUM `return` BERSYARAT MANA PUN.
   *
   * Kedua `useMemo` di bawah dulunya berdiri SESUDAH dua return di bawah ini. Render pertama
   * (`loading`) memanggil empat hook; render berikutnya (`ready`) memanggil enam — dan React
   * melempar #310 "Rendered more hooks than during the previous render", yang melepas seluruh
   * pohon menjadi layar kosong.
   *
   * Cacatnya lama, tetapi baru terlihat 26 Agustus 2026: sebelum tugas tanpa penerima dapat
   * dibuka (#833), halaman ini nyaris tidak pernah sampai ke keadaan `ready` bagi manajer.
   * Perbaikan satu tempat membuka jalan bagi cacat di tempat lain — itu bukan kebetulan,
   * melainkan bagaimana cacat yang terhalang menjadi terlihat.
   */
  const data = state.status === 'ready' ? state.data : null;
  // Konstanta modul, bukan `[]` baru tiap render: identitasnya yang dipakai daftar dependensi.
  const events = data?.events ?? EVENTS_KOSONG;
  const jejak = useMemo(() => buildTaskTrail(events), [events]);
  /*
   * Dihitung di sini, DI ATAS return bersyarat, karena `events` sudah tersedia dan aturan hook
   * menuntutnya. Ketiganya murni — tidak menyentuh jaringan maupun waktu sekarang.
   */
  const tahapan = useMemo(() => tahapanTugas(events), [events]);
  const total = useMemo(() => totalMenit(events), [events]);
  const sampai = useMemo(() => buktiSampai(events), [events]);
  const tujuan = useMemo(
    () => (data ? destinationOf(data.task) : null),
    [data?.task.destinationLatitude, data?.task.destinationLongitude],
  );

  if (state.status === 'loading') return <LoadingState label="Memuat tugas…" />;
  if (state.status === 'error') return <ErrorCard error={state.error} onRetry={reload} />;

  const { task } = state.data;
  const isAssignee = task.assigneeUserId === session.me.userId;
  const actions = availableActions({
    status: task.status as FieldTaskStatus,
    isAssignee,
    permissions: session.me.permissions,
  });
  /*
   * PERNAH MENGERJAKAN, bukan pernah muncul di riwayat. Menugaskan pun menerbitkan peristiwa
   * dengan penyelia sebagai aktornya — lihat `pernahMenjadiPelaksana` untuk cacat yang dibalik.
   */
  const pernahMengerjakan = pernahMenjadiPelaksana(events, session.me.userId);
  const reassignedAway = wasReassignedAway(
    task.assigneeUserId,
    session.me.userId,
    pernahMengerjakan,
  );
  const nav = navigationLinks({
    latitude: task.destinationLatitude,
    longitude: task.destinationLongitude,
    address: task.destinationAddress,
  });
  /*
   * Memo di atas BUKAN penghematan mikro — ia mencegah cacat yang terlihat.
   *
   * `useGeolocation` memakai `watchPosition`, jadi halaman ini render ulang setiap kali pembacaan
   * GPS bergerak. Tanpa memo, `buildTaskTrail` dan `destinationOf` menghasilkan objek BARU tiap
   * render; `useEffect` di dalam `TrailMap` bergantung pada keduanya, sehingga peta menghapus
   * seluruh lapisannya, menggambar ulang, dan MEMASANG ULANG BATAS PANDANG berkali-kali sambil
   * ditonton. Zoom dan geseran pengguna hilang setiap kali.
   */

  return (
    <>
      <section className="card">
        <div className="row space-between">
          <Link to={kembali.to}>{kembali.label}</Link>
          <span className={`badge status-${task.status}`}>{formatTaskStatus(task.status)}</span>
        </div>
        <h2>{task.title}</h2>
        <p className="muted small">
          {task.taskNumber} · {formatPriority(task.priority)}
        </p>
        {task.description ? <p>{task.description}</p> : null}
      </section>

      {reassignedAway ? (
        <section className="card" role="status">
          <p className="warning">Tugas ini bukan lagi milik Anda.</p>
          <p className="muted small">
            Penyelia mengalihkannya ke pelaksana lain. Jangan melanjutkan pengerjaan; hubungi
            penyelia Anda bila sudah terlanjur berangkat.
          </p>
        </section>
      ) : null}

      {task.destinationName || task.destinationAddress ? (
        <section className="card">
          <h3>Tujuan</h3>
          {task.destinationName ? <p>{task.destinationName}</p> : null}
          {task.destinationAddress ? <p className="small">{task.destinationAddress}</p> : null}
          {task.destinationContactName ? (
            <p className="muted small">
              Kontak: {task.destinationContactName}
              {task.destinationContactPhone ? ` · ${task.destinationContactPhone}` : ''}
            </p>
          ) : null}
          {nav.google || nav.waze ? (
            <div className="row">
              {nav.google ? (
                <a className="button-link" href={nav.google} target="_blank" rel="noreferrer">
                  Buka Google Maps
                </a>
              ) : null}
              {nav.waze ? (
                <a className="button-link" href={nav.waze} target="_blank" rel="noreferrer">
                  Buka Waze
                </a>
              ) : null}
            </div>
          ) : (
            <p className="muted small">Tujuan belum punya koordinat maupun alamat.</p>
          )}
        </section>
      ) : null}

      {task.instructions ? (
        <section className="card">
          <h3>Instruksi</h3>
          <p>{task.instructions}</p>
        </section>
      ) : null}

      {actionError ? <ErrorCard error={actionError} /> : null}

      <section className="card">
        <h3>Tindakan</h3>
        {actions.length === 0 ? (
          <p className="muted small">
            Tidak ada tindakan yang sah pada status ini. Bila Anda mengira seharusnya ada, muat
            ulang — status mungkin sudah diubah orang lain.
          </p>
        ) : null}

        {/* Akurasi tampil sebagai ANGKA sebelum kirim — centang hijau menenangkan, angka
            dapat ditindaklanjuti (pindah ke area terbuka). */}
        {actions.some(requiresLocation) ? (
          <p className={geo.status === 'ready' ? 'small' : 'warning small'}>
            {formatAccuracy(geo.location.accuracyMeters)}
            {geo.status !== 'ready' ? ' — lokasi belum siap' : ''}
          </p>
        ) : null}

        <div className="row">
          {actions.map((action) => (
            <button
              key={action}
              type="button"
              className={action === 'cancel' ? '' : 'button-primary'}
              disabled={busy !== null}
              onClick={() =>
                /*
                 * `assign` BUKAN transisi status sesaat seperti accept/start/arrive/cancel —
                 * ia menuntut MEMILIH pelaksana, dan layar itu sudah ada (`TaskAssignPage`,
                 * rute `tasks/:id/assign`). Sebelum baris ini, tombol "Tugaskan" ikut lewat
                 * `run()` yang cuma mengenal keempat aksi sesaat itu — tidak ada cabang untuk
                 * `assign`, jadi tekanannya jatuh ke `reload()` tanpa pernah memanggil API
                 * ataupun membuka pemilih pelaksana. Ditemukan 24 Sep 2026: tombolnya terlihat
                 * hidup (bisa ditekan, tidak ada galat) tapi tidak pernah melakukan apa pun —
                 * lebih menyesatkan daripada tombol yang dimatikan, karena tidak ada tanda
                 * sesuatu sedang salah.
                 */
                action === 'assign' ? navigate(`/tasks/${task.id}/assign`) : void run(action, task)
              }
            >
              {busy === action ? 'Mengirim…' : formatAction(action)}
            </button>
          ))}
        </div>

        {/*
         * Hapus berdiri TERPISAH dari deretan aksi, dan hanya muncul selama tugas belum
         * diterima kurir. Menaruhnya sebaris dengan Tugaskan/Batalkan membuat tindakan yang
         * tidak dapat dibatalkan terlihat setara dengan yang dapat.
         */}
        {canDeleteTask(task.status as FieldTaskStatus, session.me.permissions) ? (
          <div className="hapus-tugas">
            {konfirmasiHapus ? (
              <>
                <p className="warning small" role="alert">
                  Hapus tugas ini beserta daftar dokumen dan barangnya? Tindakan ini tidak dapat
                  dibatalkan.
                </p>
                <div className="row">
                  <button
                    type="button"
                    className="button-danger"
                    disabled={busy !== null}
                    onClick={() => {
                      setBusy('cancel');
                      setActionError(null);
                      void deleteTask(session.auth, task.id)
                        .then(() => navigate('/tasks/manage'))
                        .catch((caught: unknown) => {
                          setActionError(
                            caught instanceof ApiError
                              ? caught
                              : new ApiError(0, 'UNKNOWN', 'Kesalahan tidak dikenal.', null),
                          );
                          setBusy(null);
                          setKonfirmasiHapus(false);
                        });
                    }}
                  >
                    {busy !== null ? 'Menghapus…' : 'Ya, hapus'}
                  </button>
                  <button
                    type="button"
                    disabled={busy !== null}
                    onClick={() => setKonfirmasiHapus(false)}
                  >
                    Batal
                  </button>
                </div>
              </>
            ) : (
              <button type="button" className="link" onClick={() => setKonfirmasiHapus(true)}>
                Hapus tugas ini
              </button>
            )}
          </div>
        ) : null}
      </section>

      <DeliverySection task={task} taskStatus={task.status} />

      <TaskDocumentsSection taskId={task.id} />

      <section className="card">
        <h3>Riwayat</h3>

        {/*
          Peta digambar hanya bila ADA yang bisa digambar. Peta kosong yang memusat ke koordinat
          bawaan terlihat persis seperti peta yang bekerja — dan itu jenis kebohongan yang paling
          sulit dilaporkan sebagai bug.
        */}
        {/*
         * Lama tiap tahap — menjawab "kenapa pengiriman ini lama" TANPA rute.
         *
         * Diletakkan SEBELUM peta dengan sengaja: peta menggambar titik transisi yang berjarak
         * jauh, dan garis di antaranya adalah garis lurus, bukan jalan yang dilewati. Angka di
         * bawah ini yang benar-benar menjawab pertanyaannya; petanya pelengkap.
         */}
        {tahapan.length > 0 ? (
          <div className="lama-tahapan">
            <h3 className="small">Lama tiap tahap</h3>
            <dl className="lama-daftar">
              {tahapan.map((t) => (
                <div key={t.label} className="row space-between">
                  <dt>{t.label}</dt>
                  <dd>{lamaSingkat(t.menit)}</dd>
                </div>
              ))}
              {total !== null ? (
                <div className="row space-between lama-total">
                  <dt>
                    <strong>Total</strong>
                  </dt>
                  <dd>
                    <strong>{lamaSingkat(total)}</strong>
                  </dd>
                </div>
              ) : null}
            </dl>
            {sampai ? (
              <p className={sampai.dekat ? 'small' : 'warning small'}>
                Saat menekan &ldquo;tiba&rdquo;, kurir berada{' '}
                <strong>{formatDistance(sampai.jarakMeter)}</strong> dari tujuan
                {sampai.dekat ? '.' : ' — jauh dari alamat yang tercatat.'}
              </p>
            ) : (
              <p className="muted small">
                Jarak ke tujuan tidak tercatat — tujuan tugas ini tidak punya koordinat.
              </p>
            )}
          </div>
        ) : null}

        {jejak.points.length > 0 ? (
          <>
            <TrailMap points={jejak.points} destination={tujuan} />
            <p className="muted small">
              {jejak.points.length} titik kejadian
              {jejak.withoutLocation > 0
                ? ` · ${jejak.withoutLocation} event tanpa lokasi tidak digambar`
                : ''}
              . Garis putus-putus berarti jalan di antara dua titik tidak terekam.
            </p>
          </>
        ) : jejak.withoutLocation > 0 ? (
          <p className="muted small">
            {jejak.withoutLocation} event tercatat, tetapi tidak satu pun membawa lokasi — jadi
            tidak ada yang dapat dipetakan.
          </p>
        ) : null}

        {events.length === 0 ? (
          <EmptyState title="Belum ada event" hint="Riwayat terisi saat tugas mulai bergerak." />
        ) : (
          <ul className="list">
            {/* Urutan menurut sequence_no — bukan waktu perangkat, bukan waktu server. */}
            {sortEvents(events).map((event: FieldTaskEvent) => (
              <li key={event.id} className="list-item">
                <div className="row space-between">
                  <strong>{formatEventType(event.eventType)}</strong>
                  <span className="muted small">#{event.sequenceNo}</span>
                </div>
                <p className="muted small">
                  {formatDateInZone(event.occurredAtServer, timezone)}{' '}
                  {formatTimeInZone(event.occurredAtServer, timezone)}
                </p>
                {event.latitude !== null && event.longitude !== null ? (
                  <p className="muted small">
                    Titik lokasi tercatat · {formatAccuracy(event.accuracyMeters)}
                    {event.distanceToDestinationMeters !== null
                      ? ` · ${formatDistance(event.distanceToDestinationMeters)} dari tujuan`
                      : ''}
                  </p>
                ) : null}
                {lompatanMencurigakan(event.impliedSpeedKmh) ? (
                  <p className="small" style={{ color: 'var(--danger)' }}>
                    Lompatan lokasi mencurigakan — tersirat {Math.round(event.impliedSpeedKmh!)}{' '}
                    km/jam dari titik sebelumnya. Bukan bukti kecurangan (bisa juga jam perangkat
                    meleset), tapi layak ditinjau.
                  </p>
                ) : null}
                {event.note ? <p className="small">{event.note}</p> : null}
              </li>
            ))}
          </ul>
        )}
        <p className="muted small">
          Yang tercatat adalah titik kejadian pada tiap transisi, bukan jejak perjalanan — Workforce
          tidak melacak lokasi secara terus-menerus.
        </p>
      </section>
    </>
  );
}
